import { Check, LoaderCircle } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { type ComponentProps, useState } from 'react'
import { Button } from '#components/button'

/*
 * Aceternity UI stateful button (ui.aceternity.com/components/stateful-button),
 * adapted:
 * - the repo's Button look and variants instead of a green pill;
 * - the spinner and the check follow the click's own promise: a rejected one
 *   shows no check, the caller has already reported its error;
 * - no shared `layoutId`, so two of them can live on one page;
 * - lucide icons instead of hand-drawn SVG paths.
 */

const SUCCESS_MS = 900

export function StatefulButton<TResult>(
	props: Omit<ComponentProps<typeof Button>, 'onClick'> & {
		onClick: () => Promise<TResult>
		/** Runs once the check has shown, with what the click resolved to. */
		onDone?: (result: TResult) => void
	}
) {
	const { onClick, onDone, children, disabled, ...rest } = props
	const [phase, setPhase] = useState<'idle' | 'loading' | 'success'>('idle')

	const handleClick = async () => {
		setPhase('loading')
		let result: TResult
		try {
			result = await onClick()
		} catch {
			// A failed click is not a success: drop the spinner, no check.
			setPhase('idle')
			return
		}
		setPhase('success')
		setTimeout(() => {
			setPhase('idle')
			onDone?.(result)
		}, SUCCESS_MS)
	}

	return (
		<StatusButton
			{...rest}
			disabled={disabled}
			onClick={handleClick}
			phase={phase}
		>
			{children}
		</StatusButton>
	)
}

/**
 * The same spinner and check, driven from outside: for work that outlives the
 * click, like a sync an agent runs minutes later. Clicks are ignored unless
 * the button is idle.
 */
export function StatusButton(
	props: ComponentProps<typeof Button> & {
		phase: 'idle' | 'loading' | 'success'
	}
) {
	const { phase, disabled, onClick, children, ...rest } = props

	return (
		<Button
			{...rest}
			aria-busy={phase === 'loading' || undefined}
			// Busy or confirming, the button stays solid even if its action just
			// made it unavailable (a save that leaves nothing to save).
			disabled={phase === 'idle' && disabled}
			onClick={phase === 'idle' ? onClick : undefined}
		>
			{/* The icon slot opens from nothing, eating the button's 6px gap while
			    closed so the label never jumps when it goes. */}
			<motion.span
				animate={
					phase === 'idle'
						? { width: 0, marginRight: '-0.375rem', opacity: 0 }
						: { width: '1rem', marginRight: 0, opacity: 1 }
				}
				className='relative inline-flex h-4 shrink-0 overflow-hidden'
				initial={false}
				transition={{ duration: 0.2 }}
			>
				<AnimatePresence initial={false} mode='popLayout'>
					{phase === 'idle' ? null : (
						<motion.span
							animate={{ scale: 1, opacity: 1 }}
							className='inline-flex'
							exit={{ scale: 0, opacity: 0 }}
							initial={{ scale: 0, opacity: 0 }}
							key={phase}
							transition={{ duration: 0.2 }}
						>
							{phase === 'loading' ? (
								<LoaderCircle className='animate-spin' />
							) : (
								<Check />
							)}
						</motion.span>
					)}
				</AnimatePresence>
			</motion.span>
			{children}
		</Button>
	)
}
